/* eslint-disable react/no-array-index-key */
import { Availability } from '@lmc/ts-common'
import { Check, EmptyState, FormItem, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Checkbox } from 'antd'
import React from 'react'
import styled from 'styled-components'
import { capitalize } from 'lodash'

type AvailabilityPreferenceTableProps = {
  availability?: Availability
  editMode?: boolean
  testId?: string
}

const AvailabilityPreferenceTable = ({
  availability,
  editMode,
  testId,
}: AvailabilityPreferenceTableProps) => {
  if (!availability) {
    return <EmptyState title="No Availability to show" /> // this should never show up, it's here for safety
  }
  const days = Object.keys(availability) as (keyof Availability)[]

  return (
    <Table data-testid={testId ? `${testId}-table` : undefined}>
      <thead>
        <tr>
          <HeaderCell />
          {days.map((day) => (
            <HeaderCell key={day}>
              <DayText weight="medium">{capitalize(day)}</DayText>
            </HeaderCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {availability &&
          Object.values(availability[days[0]]).map((_, index) => (
            <tr key={index}>
              {days.map((day, dayIndex) => {
                const slot = availability[day][index]
                if (!slot) return <Cell key={`${day}-${index}`} />
                const fieldName = ['availability', `${day}`, index, 'available']
                return (
                  <React.Fragment key={day}>
                    {dayIndex === 0 && (
                      <DayCell>
                        <DayText weight="medium">{slot.name}</DayText>
                        <HoursText>{`${slot.startTime} - ${slot.endTime}`}</HoursText>
                      </DayCell>
                    )}
                    <Cell>
                      <CellContent
                        $selected={slot.available && !editMode}
                        $checked={slot.available && editMode}
                      >
                        {editMode ? (
                          <FormItem
                            noStyle
                            name={fieldName}
                            valuePropName="checked"
                            initialValue={slot.available}
                          >
                            <Checkbox />
                          </FormItem>
                        ) : (
                          slot.available && (
                            <Check color={theme.color.neutral.color3} />
                          )
                        )}
                      </CellContent>
                    </Cell>
                  </React.Fragment>
                )
              })}
            </tr>
          ))}
      </tbody>
    </Table>
  )
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 20px;
  overflow: hidden;
`

const HeaderCell = styled.th`
  background-color: ${(props) => props.theme.color.background.table};
  border-bottom: ${(props) => `1px solid ${props.theme.color.neutral.color7}`};
  padding: 16px 12px;
`

const Cell = styled.td`
  padding: 16px 12px;
  background-color: ${(props) => props.theme.color.neutral.color11};
`

const DayCell = styled(Cell)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: ${(props) => props.theme.color.background.table};
  border-bottom: ${(props) => `1px solid ${props.theme.color.neutral.color7}`};
  padding: 16px 24px;
`

const CellContent = styled.div<{ $selected?: boolean; $checked?: boolean }>`
  margin: 0 auto;
  height: 48px;
  width: 80px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $selected, $checked, theme }) => {
    if ($selected) return `${theme.color.status.success}4D`
    if ($checked) return `${theme.color.status.primary}4D`
    return theme.color.neutral.color8
  }};
  opacity: 0.8;
`

const DayText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const HoursText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export default AvailabilityPreferenceTable
